import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';
import {
  useAnimate2dMutation,
  useSetActiveVideoClipMutation,
  useSetFeaturedAvatarMutation,
  useVideoClipStatusQuery,
} from '@/services/avatarApi';
import {
  estimateClipCostUsd,
  getProviderMeta,
  mattingAddOnUsd,
} from '@/lib/editorCapabilities';

import { StepError } from '../components/StepError';
import { errorDetailFor, errorTitleFor } from './step6Errors';
import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  merge: (patch: Partial<WizardState>) => void;
  onComplete: () => void;
  onBackToEngine: () => void;
}

/**
 * Final review screen. Shows a summary, fires animate2d, polls
 * /video-clip-status. On success offers "Set as featured + active" and
 * the parent drops the localStorage draft.
 */
export function Step6Confirm({ state, update, merge, onComplete, onBackToEngine }: Props) {
  const { t } = useTranslation('avatars');
  const [animate2d] = useAnimate2dMutation();
  const [setActiveVideoClip] = useSetActiveVideoClipMutation();
  const [setFeatured] = useSetFeaturedAvatarMutation();

  const [phase, setPhase] = useState<'idle' | 'submitting' | 'polling' | 'ready' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const provider = useMemo(() => getProviderMeta(state.videoProvider), [state.videoProvider]);
  const baseUsd = estimateClipCostUsd(provider, state.durationSec, state.resolution);
  const totalUsd = (baseUsd + mattingAddOnUsd(state.durationSec, state.videoTransparency)) * state.variants;

  const onGenerate = useCallback(async () => {
    if (!state.avatarId) return;
    setPhase('submitting');
    setErrorMsg(null);
    try {
      const seedNum = state.videoSeed.trim().length > 0 ? Number(state.videoSeed.trim()) : undefined;
      const seed = typeof seedNum === 'number' && Number.isFinite(seedNum) && Number.isInteger(seedNum)
        ? seedNum
        : undefined;
      const res = await animate2d({
        avatarId: state.avatarId,
        prompt: state.motionPrompt,
        provider: state.videoProvider,
        durationSec: state.durationSec,
        resolution: state.resolution,
        variants: state.variants,
        transparency: state.videoTransparency,
        seed,
      }).unwrap();
      update('clipId', res.clipId);
      update('clipStatus', 'queued');
      setPhase('polling');
    } catch (err: unknown) {
      // Surface ALL info we have so a generic "Submission failed" never
      // hides the real cause. RTK Query's FetchBaseQueryError exposes
      // { status, data }; in fetch-failure cases only `error`/`message`
      // are present. Show whichever is most useful + the status code.
      console.error('[Step6Confirm] animate2d failed', err);
      const e = err as {
        status?: number;
        originalStatus?: number;
        data?: { error?: string; message?: string } | string;
        error?: string;
        message?: string;
      };
      const status = e.status ?? e.originalStatus;
      const dataObj = typeof e.data === 'object' ? e.data : undefined;
      const serverMsg =
        dataObj?.error ??
        dataObj?.message ??
        (typeof e.data === 'string' ? e.data : undefined);
      // Il messaggio del server resta com'è; si traduce solo il ripiego scritto qui.
      const fallback = e.error ?? e.message ?? t('editor.step6.submissionFailed');
      const detail = status != null ? ` (HTTP ${status})` : '';
      setErrorMsg(`${serverMsg ?? fallback}${detail}`);
      setPhase('error');
    }
  }, [animate2d, state, update, t]);

  // Poll clip status until ready/failed.
  const { data: clip } = useVideoClipStatusQuery(
    { avatarId: state.avatarId ?? '', clipId: state.clipId ?? '' },
    {
      skip: !state.avatarId || !state.clipId || phase !== 'polling',
      pollingInterval: 4000,
    },
  );

  useEffect(() => {
    if (!clip) return;
    if (clip.status === 'ready' && clip.videoUrl) {
      merge({ clipStatus: 'ready', clipUrl: clip.videoUrl });
      setPhase('ready');
    } else if (clip.status === 'failed') {
      setErrorMsg(clip.failureReason ?? t('editor.step6.clipFailed'));
      merge({ clipStatus: 'failed' });
      setPhase('error');
    }
  }, [clip, merge, t]);

  const onPublish = useCallback(async () => {
    if (!state.avatarId || !state.clipId) return;
    try {
      await setActiveVideoClip({ avatarId: state.avatarId, clipId: state.clipId }).unwrap();
      if (state.setAsFeatured) {
        await setFeatured(state.avatarId).unwrap();
      }
      onComplete();
    } catch (err) {
      console.error('[Step6Confirm] publish failed', err);
    }
  }, [state, setActiveVideoClip, setFeatured, onComplete]);

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">{t('editor.step6.title')}</h1>
        <p className="text-sm text-fg-muted">
          {t('editor.step6.intro')}
        </p>
      </header>

      {/* Summary card */}
      <div className="flex flex-col gap-3 rounded-2xl bg-bg-secondary p-4 text-sm">
        <Row label={t('editor.step6.rowTitle')} value={state.title || '—'} />
        <Row label={t('editor.step6.rowPrompt')} value={truncate(state.prompt, 120)} />
        {state.sourceImageDataUrl && <Row label={t('editor.step6.rowPhoto')} value={t('editor.step6.photoAttached')} />}
        <Row label={t('editor.step6.rowModel')} value={state.model ?? '—'} />
        <Row label={t('editor.step6.rowAspect')} value={state.aspectRatio} />
        <Row label={t('editor.step6.rowTransparent')} value={state.transparent ? t('editor.step6.yes') : t('editor.step6.no')} />
        <Row label={t('editor.step6.rowMotion')} value={truncate(state.motionPrompt, 120)} />
        <Row label={t('editor.step6.rowEngine')} value={provider.label} />
        <Row label={t('editor.step6.rowDurationRes')} value={`${state.durationSec}s · ${state.resolution}`} />
        <Row label={t('editor.step6.rowVariants')} value={`${state.variants}×`} />
        <Row label={t('editor.step6.rowBackground')} value={state.videoTransparency} />
      </div>

      {/* Generate or progress */}
      {phase === 'idle' && (
        <Button onClick={onGenerate}>{t('editor.step6.generate', { cost: totalUsd.toFixed(2) })}</Button>
      )}

      {(phase === 'submitting' || phase === 'polling') && (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-4 text-sm text-fg-muted">
          <Loader2 aria-hidden className="h-6 w-6 animate-spin text-brand" />
          <p>
            {phase === 'submitting' ? t('editor.step6.submitting') : (clip?.status === 'generating' ? t('editor.step6.generating') : t('editor.step6.queued'))}
          </p>
        </div>
      )}

      {phase === 'error' && (
        <StepError
          title={errorTitleFor(errorMsg, t)}
          detail={errorDetailFor(errorMsg, t)}
          actions={[
            { label: t('common.retry'), onClick: onGenerate, variant: 'primary' },
            { label: t('editor.step6.tweak'), onClick: onBackToEngine },
          ]}
        />
      )}

      {phase === 'ready' && state.clipUrl && (
        <>
          <div className="overflow-hidden rounded-2xl border border-black/40 bg-black">
            <video
              src={state.clipUrl}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-contain"
            />
          </div>

          <button
            type="button"
            onClick={() => update('setAsFeatured', !state.setAsFeatured)}
            className={cn(
              'flex items-center justify-between rounded-xl bg-bg-secondary px-3 py-3 text-sm transition-opacity active:opacity-80',
            )}
          >
            <span className="font-semibold text-fg">{t('editor.step6.setFeatured')}</span>
            <span
              aria-hidden
              className={cn(
                'relative h-5 w-9 rounded-full transition-colors',
                state.setAsFeatured ? 'bg-brand' : 'bg-bg-tertiary',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                  state.setAsFeatured ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </span>
          </button>

          <Button onClick={onPublish}>
            <Check aria-hidden className="h-4 w-4" />
            {t('editor.step6.saveFinish')}
          </Button>
        </>
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[11px] font-semibold uppercase text-fg-muted">{label}</span>
      <span className="text-right text-fg">{value}</span>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
